/**
 * useLayoutModel — pure (Vue-free) model for the responsive cascade layout editor.
 *
 * This is the single source of truth shared by the editor and the renderer:
 * `getMergedClasses` / `buildConfigClasses` here are the *exact* class output the
 * renderer applies to a block, so what the editor previews is byte-identical to
 * what ships. Keep this file free of Vue/admin imports — the candidate runtime
 * renderer imports it too.
 *
 * Cascade model: Mobile is the base configuration (no prefix → applies to every
 * screen). Tablet (`tablet:`) and Desktop (`desktop:`) are override steps; an
 * unauthored control inherits the nearest authored value below it. A side value
 * of `null` means "inherit / unchanged"; `0` is an explicit zero.
 *
 * Responsiveness is **container-query** based: `tablet:`/`desktop:` are custom
 * variants (defined in each app's tailwind.css) that resolve against the render
 * width — the nearest ancestor carrying the `@container` utility (the render
 * root in resource-renderer.vue) — not the browser viewport. This is what lets
 * the resource editor's device toggle preview accurately by just resizing the
 * preview container, with no iframe. Widths mirror the former md/lg breakpoints
 * (768px / 1024px).
 */

export interface LayoutType {
  $type: string;
  [key: string]: any;
}
export interface LayoutConfig {
  $configuration: string;
  types: LayoutType[];
}
export type Layout = LayoutConfig[];

export interface DeviceDef {
  key: string;
  label: string;
  range: string;
  prefix: string;
  icon: string;
}

// Configurations. Mobile = base (no prefix → all screens). Order = cascade order.
export const DEVICES: DeviceDef[] = [
  { key: "Mobile", label: "Mobile", range: "All screens", prefix: "", icon: "lucide:smartphone" },
  { key: "Tablet", label: "Tablet", range: "Tablet & up", prefix: "tablet:", icon: "lucide:tablet" },
  { key: "Desktop", label: "Desktop", range: "Desktop & up", prefix: "desktop:", icon: "lucide:monitor" },
];

export const deviceIndex = (k: string) => DEVICES.findIndex((d) => d.key === k);

/**
 * Map a configuration name to its container-query variant prefix. Tablet and
 * Desktop emit the `tablet:`/`desktop:` custom variants (container queries at
 * 768px / 1024px); Mobile is the unprefixed base. Tolerant of casing and
 * legacy/alias names ("mobile"/"tablet"/"desktop", "sm"/"md"/"lg"/"xl") so
 * previously-stored layout data keeps rendering. Legacy viewport aliases map to
 * the nearest device step (sm/md → tablet, lg/xl → desktop) so they still
 * resolve against a container context instead of emitting orphaned viewport
 * classes that would never match without one.
 */
export const prefixFor = (k: string): string => {
  const key = (k || "").toString().trim().toLowerCase();
  switch (key) {
    case "":
    case "xs":
    case "mobile":
      return "";
    case "sm":
    case "tablet":
    case "md":
      return "tablet:";
    case "lg":
    case "desktop":
    case "xl":
    case "2xl":
      return "desktop:";
    default:
      return "";
  }
};
export const FRAME_WIDTH: Record<string, number> = { Mobile: 190, Tablet: 264, Desktop: 336 };

// Tailwind spacing scale ↔ friendly presets. scale n === n*4 px.
export interface PresetDef {
  key: string;
  label: string;
  scale: number;
}
export const PRESETS: PresetDef[] = [
  { key: "none", label: "None", scale: 0 },
  { key: "s", label: "S", scale: 2 },
  { key: "m", label: "M", scale: 4 },
  { key: "l", label: "L", scale: 8 },
  { key: "xl", label: "XL", scale: 12 },
];

// Plain-English groupings shown as section dividers + the +Add menu.
export interface GroupDef {
  key: string;
  label: string;
}
export const GROUPS: GroupDef[] = [
  { key: "Spacing", label: "Spacing" },
  { key: "Size & grid", label: "Size & grid" },
  { key: "Appearance", label: "Appearance" },
  { key: "Visibility", label: "Visibility" },
  { key: "Advanced", label: "Advanced" },
];

/**
 * CONTROL REGISTRY — the extensibility seam. To add a new mini-editor:
 *   1) add an entry here, 2) add its component to the EDITORS map (in the editor),
 *   3) add a build() branch in buildConfigClasses. It then appears in the +Add
 *      menu, the cascade, the preview and the generated classes automatically.
 */
export interface ControlDef {
  type: string;
  label: string;
  group: string;
  icon: string;
  blurb: string;
  make: () => LayoutType;
  isSet: (v: any) => boolean;
}
export const CONTROL_DEFS: Record<string, ControlDef> = {
  padding: {
    type: "padding",
    label: "Padding",
    group: "Spacing",
    icon: "lucide:square-dashed",
    blurb: "Space inside the block, around its content.",
    make: () => ({ $type: "padding", top: null, right: null, bottom: null, left: null }),
    isSet: (v) => [v.top, v.right, v.bottom, v.left].some((x) => x != null),
  },
  margin: {
    type: "margin",
    label: "Margin",
    group: "Spacing",
    icon: "lucide:scan",
    blurb: "Space outside the block, pushing neighbors away.",
    make: () => ({ $type: "margin", top: null, right: null, bottom: null, left: null }),
    isSet: (v) => [v.top, v.right, v.bottom, v.left].some((x) => x != null),
  },
  spacing: {
    type: "spacing",
    label: "Gap",
    group: "Spacing",
    icon: "lucide:panel-bottom",
    blurb: "Even space between child items.",
    make: () => ({ $type: "spacing", gap: null }),
    isSet: (v) => v.gap != null,
  },
  grid: {
    type: "grid",
    label: "Columns",
    group: "Size & grid",
    icon: "lucide:grid-2x2",
    blurb: "Lay children out in equal columns.",
    make: () => ({ $type: "grid", columns: null }),
    isSet: (v) => v.columns != null,
  },
  background: {
    type: "background",
    label: "Background",
    group: "Appearance",
    icon: "lucide:paintbrush",
    blurb: "Fill the block with a color.",
    make: () => ({ $type: "background", color: "" }),
    isSet: (v) => !!v.color,
  },
  state: {
    type: "state",
    label: "Visibility",
    group: "Visibility",
    icon: "lucide:eye",
    blurb: "Show or hide the block; disable interaction.",
    make: () => ({ $type: "state", visible: true, enabled: true }),
    isSet: (v) => v.visible === false || v.enabled === false,
  },
  raw: {
    type: "raw",
    label: "Custom classes",
    group: "Advanced",
    icon: "lucide:code",
    blurb: "Type raw Tailwind classes.",
    make: () => ({ $type: "raw", classes: "" }),
    isSet: (v) => !!(v.classes && v.classes.trim()),
  },
};
export const CONTROL_ORDER = ["padding", "margin", "spacing", "grid", "background", "state", "raw"];

export const EDITORS: Record<string, string> = {
  padding: "BoxEditor",
  margin: "BoxEditor",
  spacing: "GapEditor",
  grid: "GridEditor",
  background: "BackgroundEditor",
  state: "StateEditor",
  raw: "RawEditor",
};
export const SUPPORTS_EXACT: Record<string, boolean> = {
  padding: true,
  margin: true,
  spacing: true,
  grid: true,
  background: true,
};

// ── class builder (faithful port of the renderer's buildConfigClasses) ──
const isNum = (x: any): x is number => typeof x === "number" && Number.isFinite(x);

function addDirectional(arr: string[], p: string, base: "m" | "p", d: any) {
  if (isNum(d.top)) arr.push(`${p}${base}t-${d.top}`);
  if (isNum(d.right)) arr.push(`${p}${base}r-${d.right}`);
  if (isNum(d.bottom)) arr.push(`${p}${base}b-${d.bottom}`);
  if (isNum(d.left)) arr.push(`${p}${base}l-${d.left}`);
}

export function buildConfigClasses(config: LayoutConfig): string[] {
  const p = prefixFor(config.$configuration);
  const cls: string[] = [];
  const find = (t: string) => config.types?.find((x) => x.$type === t);
  const st = find("state");
  if (st) {
    if (st.visible === false) cls.push(p + "hidden");
    if (st.enabled === false) cls.push("pointer-events-none");
  }
  const m = find("margin");
  if (m) addDirectional(cls, p, "m", m);
  const pd = find("padding");
  if (pd) addDirectional(cls, p, "p", pd);
  const sp = find("spacing");
  if (isNum(sp?.gap)) cls.push(p + `gap-${sp!.gap}`);
  const g = find("grid");
  if (Number.isInteger(g?.columns) && g!.columns >= 1) {
    cls.push(p + "grid", p + `grid-cols-${g!.columns}`);
  }
  const bg = find("background");
  if (bg?.color) cls.push(p + `bg-${bg.color}`);
  const rw = find("raw");
  if (rw?.classes && rw.classes.trim()) cls.push(rw.classes.trim());
  return cls;
}

export const getMergedClasses = (layout: Layout): string =>
  (layout ?? []).flatMap(buildConfigClasses).join(" ").trim();

export const groupedClasses = (layout: Layout) =>
  DEVICES.map((d) => {
    const c = layout.find((x) => x.$configuration === d.key);
    return { device: d, classes: c ? buildConfigClasses(c) : [] };
  }).filter((g) => g.classes.length);

/** Legacy object layout → array of typed entries (back-compat with v1 blocks). */
export function transformLegacy(obj: any): LayoutType[] {
  const types: LayoutType[] = [];
  if (obj.visible !== undefined || obj.enabled !== undefined)
    types.push({ $type: "state", visible: obj.visible !== false, enabled: obj.enabled !== false });
  if (obj.margin) types.push({ $type: "margin", ...obj.margin });
  if (obj.padding) types.push({ $type: "padding", ...obj.padding });
  if (obj.backgroundColor) types.push({ $type: "background", color: obj.backgroundColor });
  if (obj.rawCss) types.push({ $type: "raw", classes: obj.rawCss });
  return types;
}

/** Normalize any stored layout value to the array form the model operates on. */
export function getEffectiveLayout(raw: any): Layout {
  if (!raw) return [];
  if (!Array.isArray(raw)) return [{ $configuration: "Mobile", types: transformLegacy(raw) }];
  return raw;
}

export function applyLayoutStateOverrides(
  raw: unknown,
  overrides: { visible?: boolean; enabled?: boolean },
): Layout {
  const layout = getEffectiveLayout(raw).map((configuration) => ({
    ...configuration,
    types: (configuration.types ?? []).map((type) => ({ ...type })),
  }));

  if (overrides.visible === undefined && overrides.enabled === undefined) {
    return layout;
  }

  for (const configuration of layout) {
    const state = configuration.types.find((type) => type.$type === "state");
    if (!state) continue;
    if (overrides.visible !== undefined) delete state.visible;
    if (overrides.enabled !== undefined) delete state.enabled;
  }

  let mobile = layout.find((configuration) => configuration.$configuration === "Mobile");
  if (!mobile) {
    mobile = { $configuration: "Mobile", types: [] };
    layout.unshift(mobile);
  }

  let state = mobile.types.find((type) => type.$type === "state");
  if (!state) {
    state = { $type: "state" };
    mobile.types.push(state);
  }

  if (overrides.visible !== undefined) state.visible = overrides.visible;
  if (overrides.enabled !== undefined) state.enabled = overrides.enabled;
  return layout;
}

// ── inheritance resolution (the heart of the cascade) ──
export const configFor = (layout: Layout, k: string) =>
  layout.find((c) => c.$configuration === k);

/**
 * The control object authored AT this device (present in its `types[]`), or null —
 * regardless of whether it currently holds a value. Use this (not `ownValueAt`) to
 * decide whether a device shows its own card for a control, so a freshly-added but
 * still-empty control stays visible for the user to fill in (design doc §9).
 */
export const typeIn = (layout: Layout, k: string, t: string): LayoutType | null =>
  configFor(layout, k)?.types.find((x) => x.$type === t) ?? null;

const ownValue = (layout: Layout, k: string, t: string) => {
  const x = configFor(layout, k)?.types.find((y) => y.$type === t);
  return x && CONTROL_DEFS[t].isSet(x) ? x : null;
};

export interface EffectiveResult {
  value: LayoutType;
  source: string | null;
  inherited: boolean;
}
/** Walk device→…→Mobile; first authored value wins. */
export function resolveEffective(layout: Layout, device: string, type: string): EffectiveResult {
  for (let i = deviceIndex(device); i >= 0; i--) {
    const v = ownValue(layout, DEVICES[i].key, type);
    if (v) return { value: v, source: DEVICES[i].key, inherited: DEVICES[i].key !== device };
  }
  return { value: CONTROL_DEFS[type].make(), source: null, inherited: false };
}

/** The own (authored-at-this-device) value, or null when inherited/unset. */
export const ownValueAt = ownValue;

/**
 * Control types that are effectively in play (authored with a real value) at or
 * below a device — i.e. the controls a higher layer inherits. An unset control
 * (present but `isSet` false) does NOT count, so it never reads as an inherited
 * value downstream (design doc §9).
 */
export function activeTypesAt(layout: Layout, device: string): string[] {
  const set = new Set<string>();
  for (let i = 0; i <= deviceIndex(device); i++)
    configFor(layout, DEVICES[i].key)?.types.forEach((t) => {
      if (CONTROL_DEFS[t.$type]?.isSet(t)) set.add(t.$type);
    });
  return CONTROL_ORDER.filter((t) => set.has(t));
}

// ── mutations (immutable; return a fresh layout) ──
const clone = <T>(l: T): T => JSON.parse(JSON.stringify(l));

export function ensureConfig(l: Layout, d: string): Layout {
  l = clone(l);
  if (!configFor(l, d)) {
    l.push({ $configuration: d, types: [] });
    l.sort((a, b) => deviceIndex(a.$configuration) - deviceIndex(b.$configuration));
  }
  return l;
}
export function setType(l: Layout, d: string, obj: LayoutType): Layout {
  l = ensureConfig(l, d);
  const c = configFor(l, d)!;
  const i = c.types.findIndex((t) => t.$type === obj.$type);
  if (i >= 0) c.types[i] = obj;
  else c.types.push(obj);
  return l;
}
export function addType(l: Layout, d: string, t: string): Layout {
  const e = resolveEffective(l, d, t);
  return setType(l, d, e.source != null ? clone(e.value) : CONTROL_DEFS[t].make());
}
export function removeType(l: Layout, d: string, t: string): Layout {
  l = clone(l);
  const c = configFor(l, d);
  if (c) c.types = c.types.filter((x) => x.$type !== t);
  return l;
}
export const overrideType = addType; // seed an override from the inherited value
export function addDevice(l: Layout, d: string): Layout {
  return ensureConfig(l, d);
}
export function removeDevice(l: Layout, d: string): Layout {
  return clone(l).filter((c) => c.$configuration !== d);
}
export const activeDevices = (l: Layout) =>
  DEVICES.filter((d) => l.some((c) => c.$configuration === d.key));

// ── background token → preview color ──
export const BG_COLORS: Record<string, string> = {
  "": "transparent",
  white: "#fff",
  "slate-50": "#f8fafc",
  "slate-100": "#f1f5f9",
  "slate-200": "#e2e8f0",
  "gray-50": "#f9fafb",
  "gray-100": "#f3f4f6",
  "primary-50": "color-mix(in oklch, var(--primary) 12%, white)",
  "primary-100": "color-mix(in oklch, var(--primary) 22%, white)",
};
export const bgColor = (t: string) => BG_COLORS[t] ?? "#eef2ff";
export const BG_SWATCHES = [
  "white",
  "slate-50",
  "slate-100",
  "slate-200",
  "gray-50",
  "gray-100",
  "primary-50",
  "primary-100",
];

/** Short human summary (used in inherited chips / collapsed states). */
export function summaryFor(type: string, v: any): string {
  switch (type) {
    case "padding":
    case "margin": {
      const a = [v.top, v.right, v.bottom, v.left];
      if (a.every((x) => x == null)) return "none";
      if (a.every((x) => x === a[0])) return `${a[0] * 4}px all`;
      return a
        .map((x, i) => (x != null ? `${"TRBL"[i]}${x * 4}` : ""))
        .filter(Boolean)
        .join(" ");
    }
    case "spacing":
      return v.gap != null ? `${v.gap * 4}px` : "none";
    case "grid":
      return v.columns ? `${v.columns} cols` : "none";
    case "background":
      return v.color || "none";
    case "state":
      return (
        [v.visible === false && "hidden", v.enabled === false && "not interactive"]
          .filter(Boolean)
          .join(", ") || "visible"
      );
    case "raw":
      return v.classes ? `${v.classes.trim().split(/\s+/).length} class(es)` : "none";
    default:
      return "";
  }
}
